import useNonInitialEffect from '@versiondos/hooks';
import { fetchRefundsByInvoiceThunk, fetchPaymentsByInvoiceThunk } from '@spike/payments-action';
import clsx from 'clsx';
import { Spinner } from 'components/UI';
import { Payment, Refund } from '@spike/payments-model';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PaymentsStatus } from '@spike/payments-action';
import { RootState } from 'store';
import { useApiClientWrapper } from 'hooks';
import TransactionsEmpty from './TransactionsEmpty';
import styles from './Transactions.module.css';
import PaymentTransaction from './PaymentTransaction';
import RefundTransaction from './RefundTransaction';

interface TransactionsProps {
    invoiceId: number;
    className?: string;
    onPay: () => void;
    onViewPayment: (payment: Payment) => void;
}

export interface TransactionsWrapper {
    payments: Array<Payment>;
    refunds: Array<Refund>;
}

export const Transactions: FunctionComponent<TransactionsProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const payments = useSelector<RootState, Array<Payment>>(state => state.payments.paymentsByInvoice);

    const refunds = useSelector<RootState, Array<Refund>>(state => state.payments.refundsByInvoice);

    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);

    const [loading, setLoading] = useState(true);

    const [transactions, setTransactions] = useState<Array<any>>([]);

    useEffect(() => {
        dispatch(fetchPaymentsByInvoiceThunk(apiClientWrapper, props.invoiceId));
        setLoading(true);
    }, []);

    useNonInitialEffect(() => {
        switch (paymentsStatus) {
            case PaymentsStatus.FetchPaymentsByInvoiceSuccess:
                dispatch(fetchRefundsByInvoiceThunk(apiClientWrapper, props.invoiceId));
                setLoading(true);
                break;
            case PaymentsStatus.FetchRefundsByInvoiceSuccess:
                setLoading(false);
                setTransactions(getSortedTransactions());
                break;
            case PaymentsStatus.Error:
                setLoading(false);
                break;
        }
    }, [paymentsStatus]);

    const getSortedTransactions = () => {
        const paymentsList = payments || [];
        const refundsList = refunds || [];

        const mappedPayments = paymentsList.map(payment => ({
            ...payment,
            type: 'payment',
            date: payment.createdAt
        }));

        const mappedRefunds = refundsList.map(refund => ({
            ...refund,
            type: 'refund',
            date: refund.refundDate
        }));

        const allTransactions = [...mappedPayments, ...mappedRefunds];
        allTransactions.sort((a, b) => {
            if (a.date.isBefore(b.date)) return 1;
            if (a.date.isAfter(b.date)) return -1;
            return 0;
        });

        return allTransactions;
    };

    return (
        <>
            {loading && <Spinner />}
            {!loading && transactions.length === 0 && <TransactionsEmpty onPay={props.onPay} />}
            {!loading && transactions.length > 0 && (
                <div className={clsx(styles.container, props.className)}>
                    {transactions.map((transaction: any) => (
                        <div
                            className={styles.transaction}
                            key={transaction.id}
                        >
                            {transaction.type === 'payment' ? (
                                <PaymentTransaction
                                    key={transaction.id}
                                    payment={transaction}
                                    onViewPayment={props.onViewPayment}
                                />
                            ) : (
                                <RefundTransaction
                                    key={transaction.id}
                                    refund={transaction}
                                />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

export default Transactions;
